import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export type Language = 'en' | 'zh';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // 从路由中获取语言
  const getLanguageFromPath = useCallback(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const langCode = pathSegments[0];
    if (langCode === 'zh' || langCode === 'en') {
      return langCode;
    }
    return null;
  }, [location.pathname]);

  const [language, setLanguageState] = useState<Language>(() => {
    // 优先从路由获取语言
    const routeLang = getLanguageFromPath();
    if (routeLang) {
      return routeLang;
    }
    
    // 其次从localStorage获取
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage === 'en' || savedLanguage === 'zh') {
      return savedLanguage;
    }
    
    // 最后使用浏览器语言
    return navigator.language.startsWith('zh') ? 'zh' : 'en';
  });

  const setLanguage = useCallback((newLang: Language) => {
    localStorage.setItem('language', newLang);
    document.documentElement.lang = newLang;
    setLanguageState(newLang);

    // 更新路由
    const currentPath = location.pathname;
    const pathSegments = currentPath.split('/').filter(Boolean);
    
    // 移除当前的语言前缀
    if (pathSegments[0] === 'zh' || pathSegments[0] === 'en') {
      pathSegments.shift();
    }
    
    // 添加新的语言前缀
    const newPath = `/${newLang}${pathSegments.length > 0 ? '/' + pathSegments.join('/') : ''}`;
    navigate(newPath, { replace: true });
  }, [navigate, location.pathname]);

  // 当路由变化时更新语言状态
  useEffect(() => {
    const routeLang = getLanguageFromPath();
    if (routeLang && routeLang !== language) {
      setLanguageState(routeLang);
      localStorage.setItem('language', routeLang);
      document.documentElement.lang = routeLang;
    }
  }, [location.pathname, language, getLanguageFromPath]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 